import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Eye, AlertCircle } from 'lucide-react';
import { loansApi } from '../../api';
import { Loan } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { StatusBadge } from '../../components/StatusBadge';
import { Button } from '../../components/Button';
import { formatCurrency } from '../../utils/currency';

export const MyLoans: React.FC = () => {
  const navigate = useNavigate();
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const response = await loansApi.getAll();
        if (response.success && response.data) {
          setLoans(response.data);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load loans');
      } finally {
        setLoading(false);
      }
    };

    fetchLoans();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="loans-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>My Loans</h1>
          <p>View and manage all your active and completed loans</p>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      {loans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Wallet size={64} />
          </div>
          <h3>No Loans Yet</h3>
          <p>You don't have any loans. Submit a loan application to get started with fast, flexible financing.</p>
          <Button onClick={() => navigate('/customer/applications/new')} variant="primary" size="lg">
            Apply for Your First Loan
          </Button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Product</th>
                <th>Principal</th>
                <th>Outstanding Balance</th>
                <th>Total Repaid</th>
                <th>Status</th>
                <th>Date Disbursed</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loans.map((loan) => (
                <tr key={loan.loan_id}>
                  <td className="font-mono">{loan.loan_reference}</td>
                  <td className="font-medium">{loan.product_name || 'N/A'}</td>
                  <td className="font-semibold">
                    {formatCurrency(loan.principal_amount)}
                  </td>
                  <td className="font-semibold">
                    {formatCurrency(
                      parseFloat(loan.outstanding_principal) +
                      parseFloat(loan.outstanding_interest)
                    )}
                  </td>
                  <td>
                    {formatCurrency(loan.total_paid)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <StatusBadge
                        status={loan.is_overdue ? 'overdue' : loan.status}
                      />
                      {loan.is_overdue && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: 'var(--color-error)' }}>
                          <AlertCircle size={12} />
                          Overdue
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    {new Date(loan.disbursement_date).toLocaleDateString('en-NG', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </td>
                  <td>
                    <Button
                      onClick={() => navigate(`/customer/loans/${loan.loan_id}`)}
                      variant="primary"
                      size="sm"
                      icon={<Eye size={16} />}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        /* Tablet Responsive Styles */
        @media (max-width: 1024px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .table-container {
            overflow-x: auto;
          }

          .data-table {
            min-width: 900px;
          }
        }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .loans-page {
            padding: 1rem;
          }

          .page-header h1 {
            font-size: 1.75rem;
          }

          .page-header p {
            font-size: 0.9375rem;
          }

          .table-container {
            border-radius: 0.75rem;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .data-table {
            min-width: 1000px;
            font-size: 0.875rem;
          }

          .data-table th,
          .data-table td {
            padding: 0.75rem 0.5rem;
          }

          .data-table th:first-child,
          .data-table td:first-child {
            position: sticky;
            left: 0;
            background-color: white;
            z-index: 1;
          }

          .data-table th:first-child {
            background-color: var(--color-neutral-50);
          }

          .empty-state {
            padding: 2rem 1rem;
          }

          .empty-state h3 {
            font-size: 1.25rem;
          }

          .empty-state p {
            font-size: 0.9375rem;
          }

          .empty-state-icon svg {
            width: 48px;
            height: 48px;
          }
        }

        /* Small Mobile Responsive Styles */
        @media (max-width: 480px) {
          .loans-page {
            padding: 0.875rem;
          }

          .page-header h1 {
            font-size: 1.5rem;
          }

          .data-table {
            font-size: 0.8125rem;
          }

          .empty-state {
            padding: 1.5rem 0.875rem;
          }

          .empty-state-icon svg {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>
    </div>
  );
};
